import { useState, useEffect } from 'react';
import Modal from '../shared/Modal';
import { getBlockTemplates, saveBlockTemplates } from '../../utils/api';

export default function BlockTemplateModal({ open, onClose, teamId, subRole, showToast }) {
  const [templates, setTemplates] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [newLabels, setNewLabels] = useState({});
  const [newQtys, setNewQtys] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    if (!open || !teamId) return;
    setLoading(true);
    getBlockTemplates(teamId, subRole)
      .then(data => {
        const list = data || [];
        setTemplates(list);
        // Auto-expand if there's only one template
        if (list.length === 1) setExpandedId(list[0].id);
      })
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, [open, teamId, subRole]);

  function addTemplate() {
    const id = 'tpl-' + Date.now();
    const newTpl = { id, name: '', positions: [] };
    setTemplates(prev => [...prev, newTpl]);
    setExpandedId(id);
  }

  function deleteTemplate(id) {
    setTemplates(prev => prev.filter(t => t.id !== id));
    if (expandedId === id) setExpandedId(null);
  }

  function updateName(id, name) {
    setTemplates(prev => prev.map(t => t.id === id ? { ...t, name } : t));
  }

  function addPosition(templateId) {
    const label = (newLabels[templateId] || '').trim();
    if (!label) return;
    const qty = Math.max(1, parseInt(newQtys[templateId]) || 1);
    setTemplates(prev => prev.map(t =>
      t.id === templateId
        ? { ...t, positions: [...t.positions, { label, qty }] }
        : t
    ));
    setNewLabels(prev => ({ ...prev, [templateId]: '' }));
    setNewQtys(prev => ({ ...prev, [templateId]: 1 }));
  }

  function removePosition(templateId, idx) {
    setTemplates(prev => prev.map(t =>
      t.id === templateId
        ? { ...t, positions: t.positions.filter((_, i) => i !== idx) }
        : t
    ));
  }

  async function handleSave() {
    // Validate: all templates must have a name
    const unnamed = templates.find(t => !t.name.trim());
    if (unnamed) {
      showToast('All templates need a name.');
      setExpandedId(unnamed.id);
      return;
    }
    setSaving(true);
    try {
      await saveBlockTemplates(teamId, templates, subRole);
      showToast('Templates saved.');
      onClose();
    } catch (err) {
      showToast('Error saving templates.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  function handleClose() {
    setExpandedId(null);
    setNewLabels({});
    setNewQtys({});
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={subRole ? `Block Templates — ${subRole}` : 'Block Templates'}
      wide
      footer={
        <>
          <button className="btn" onClick={handleClose}>Cancel</button>
          <button className="btn btn-dark" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving…' : 'Save Templates'}
          </button>
        </>
      }
    >
      <p style={{ fontFamily: 'var(--font-b)', fontSize: 14, fontWeight: 300, color: 'var(--mid)', marginBottom: 16 }}>
        Create named templates with preset positions. When adding a block, choose which template to apply.
      </p>

      {loading ? (
        <div style={{ padding: '24px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>
          Loading templates…
        </div>
      ) : (
        <>
          {templates.length === 0 && (
            <div style={{ padding: '28px 24px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300, border: '1px dashed var(--border)', borderRadius: 'var(--radius)', marginBottom: 12 }}>
              No templates yet — click <strong style={{ fontFamily: 'var(--font-h)', color: 'var(--black)' }}>+ Add Template</strong> to create one.
            </div>
          )}

          {templates.map(tpl => {
            const isExpanded = expandedId === tpl.id;
            const slotCount = tpl.positions.reduce((s, p) => s + (p.qty || 1), 0);
            return (
              <div key={tpl.id} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginBottom: 10, overflow: 'hidden' }}>
                {/* Header row */}
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--off)', cursor: 'pointer' }}
                  onClick={() => setExpandedId(isExpanded ? null : tpl.id)}
                >
                  <span style={{ color: 'var(--muted)', fontSize: 10, flexShrink: 0, width: 10 }}>
                    {isExpanded ? '▼' : '▶'}
                  </span>
                  <input
                    className="form-input"
                    style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, flex: 1, padding: '4px 8px', height: 'auto' }}
                    placeholder="Template name (e.g. Standard, Skeleton Crew…)"
                    value={tpl.name}
                    onChange={e => { e.stopPropagation(); updateName(tpl.id, e.target.value); }}
                    onClick={e => e.stopPropagation()}
                  />
                  <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)', flexShrink: 0, whiteSpace: 'nowrap' }}>
                    {slotCount} slot{slotCount !== 1 ? 's' : ''}
                  </span>
                  <button
                    className="btn btn-xs btn-danger"
                    style={{ flexShrink: 0 }}
                    onClick={e => { e.stopPropagation(); setConfirmDeleteId(confirmDeleteId === tpl.id ? null : tpl.id); }}
                  >
                    Delete
                  </button>
                </div>

                {/* Inline delete confirm */}
                {confirmDeleteId === tpl.id && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#fff5f5', borderTop: '1px solid #fcc' }}>
                    <span style={{ fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 500, color: 'var(--black)', flex: 1 }}>
                      Delete "{tpl.name || 'this template'}"?
                    </span>
                    <button className="btn btn-xs" onClick={e => { e.stopPropagation(); setConfirmDeleteId(null); }}>Cancel</button>
                    <button className="btn btn-xs btn-danger" onClick={e => { e.stopPropagation(); deleteTemplate(tpl.id); setConfirmDeleteId(null); }}>Yes, Delete</button>
                  </div>
                )}

                {/* Expanded positions editor */}
                {isExpanded && (
                  <div>
                    {tpl.positions.length === 0 && (
                      <div style={{ padding: '12px 16px', fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: 'var(--muted)' }}>
                        No positions yet — add one below.
                      </div>
                    )}
                    {tpl.positions.map((pos, idx) => (
                      <div
                        key={idx}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 16px', borderBottom: '1px solid var(--border)', background: 'var(--white)' }}
                      >
                        <span style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>
                          {pos.label}
                          {pos.qty > 1 && (
                            <span style={{ fontFamily: 'var(--font-b)', fontWeight: 300, color: 'var(--muted)', marginLeft: 6 }}>×{pos.qty}</span>
                          )}
                        </span>
                        <button
                          className="btn-ghost btn-xs"
                          onClick={() => removePosition(tpl.id, idx)}
                          style={{ color: 'var(--danger, #c00)', fontSize: 12 }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    {/* Add position row */}
                    <div style={{ padding: 10, background: 'var(--off)', borderTop: tpl.positions.length > 0 ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input
                          className="form-input"
                          placeholder="Position name…"
                          value={newLabels[tpl.id] || ''}
                          onChange={e => setNewLabels(prev => ({ ...prev, [tpl.id]: e.target.value }))}
                          onKeyDown={e => e.key === 'Enter' && addPosition(tpl.id)}
                          style={{ flex: 1 }}
                        />
                        <input
                          className="form-input"
                          type="number"
                          placeholder="Qty"
                          value={newQtys[tpl.id] ?? 1}
                          min="1"
                          max="20"
                          onChange={e => setNewQtys(prev => ({ ...prev, [tpl.id]: Math.max(1, parseInt(e.target.value) || 1) }))}
                          style={{ width: 64 }}
                        />
                        <button
                          className="btn btn-sm btn-dark"
                          onClick={() => addPosition(tpl.id)}
                          disabled={!(newLabels[tpl.id] || '').trim()}
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <button className="btn btn-sm" style={{ marginTop: 4 }} onClick={addTemplate}>
            + Add Template
          </button>
        </>
      )}
    </Modal>
  );
}
